import { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from 'stores/auth-store';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => {
      const authStore = useAuthStore();
      return authStore.isMember() ? '/profile' : '/groups';
    },
  },
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        name: 'home',
        path: '',
        redirect: '/profile',
      },
      {
        name: 'search',
        path: 'search',
        component: () => import('pages/search/IndexPage.vue'),
      },
      {
        name: 'membership',
        path: 'membership',
        component: () => import('pages/membership/IndexPage.vue'),
        meta: {
          can: 'MEMBERSHIP_CHALLENGE:ALLOW:VIEW_ANY',
        },
      },
      {
        name: 'members',
        path: 'members',
        component: () => import('pages/members/IndexPage.vue'),
        meta: {
          can: 'PEOPLE:ALLOW:VIEW_ANY',
        },
      },
      {
        name: 'registrations',
        path: 'registrations',
        component: () => import('pages/registrations/IndexPage.vue'),
        meta: {
          can: 'REGISTRATIONS:ALLOW:VIEW_ANY',
        },
      },
      {
        name: 'acls',
        path: 'acls',
        component: () => import('pages/acls/ACLDashboard.vue'),
        meta: {
          can: 'ROLES:ALLOW:VIEW_ANY',
        },
        children: [
          {
            name: 'acls-diagnostics-roles',
            path: 'diagnostics/roles',
            component: () =>
              import('pages/acls/diagnostics/RolesDiagnosticsList.vue'),
          },
          {
            name: 'acls-diagnostics-permissions',
            path: 'diagnostics/permissions',
            component: () =>
              import('pages/acls/diagnostics/PermissionsDiagnosticsList.vue'),
          },
          {
            name: 'acls-office-posts',
            path: 'office-posts',
            component: () =>
              import('pages/acls/office-post-roles/OfficePostRolesList.vue'),
          },
          {
            name: 'acls-ranks',
            path: 'ranks',
            component: () =>
              import('pages/acls/rank-type-roles/RankTypeRolesList.vue'),
          },
          {
            name: 'acls-manage',
            path: 'manage',
            component: () =>
              import('pages/acls/rank-type-roles/RankTypeRolesList.vue'),
          },
          {
            name: 'acls-permissions',
            path: 'permissions',
            component: () =>
              import('pages/acls/permissions/PermissionsManage.vue'),
          },
        ],
      },
      {
        path: '/support-centre',
        meta: {
          requiredFeatureFlag: 'help-centre',
        },
        children: [
          {
            path: ':slug?',
            name: 'support-centre',
            component: () => import('pages/support-centre/IndexPage.vue'),
            props: (route) => ({ slug: route.params.slug }),
          },
          {
            path: ':slug/requests/new',
            name: 'create-support-ticket',
            component: () => import('pages/support-centre/CreateTicket.vue'),
            props: (route) => ({ slug: route.params.slug }),
          },
        ],
      },
      {
        path: 'support-requests',
        meta: {
          requiredFeatureFlag: 'help-centre',
        },
        children: [
          {
            path: '',
            name: 'support-requests',
            component: () => import('src/pages/support-requests/IndexPage.vue'),
            children: [
              {
                path: ':ticketId(\\d+)',
                name: 'support-request',
                component: () =>
                  import('src/pages/support-requests/TicketDetail.vue'),
                props: (route) => ({ ticketId: Number(route.params.ticketId) }),
              },
            ],
          },
        ],
      },
      {
        path: '/editor',
        component: () => import('layouts/BlankLayout.vue'),
        children: [
          {
            path: '',
            name: 'editor',
            component: () => import('pages/editor/IndexPage.vue'),
          },
        ],
      },
      {
        path: '/preview',
        component: () => import('layouts/BlankLayout.vue'),
        children: [
          {
            path: ':id',
            name: 'preview',
            component: () => import('pages/preview/IndexPage.vue'),
            props: true,
          },
        ],
      },

      {
        name: 'groups',
        path: 'groups',
        component: () => import('pages/groups/IndexPage.vue'),
        meta: {
          can: 'UNITS:ALLOW:VIEW_ANY',
        },
      },
      {
        name: 'annual-returns',
        path: 'annual-returns',
        component: () => import('pages/annual-returns/IndexPage.vue'),
        meta: {
          // The admin listing is for regional/organisational administrators
          // only. Unit-level managers use the my-returns dashboard instead.
          can: [
            'ANNUAL_RETURNS:ALLOW:MANAGE_REGION',
            'ANNUAL_RETURNS:ALLOW:MANAGE_ORGANISATION',
          ],
          // the route will only be supported when this feature flag is turned on for the current user
          requiredFeatureFlag: 'annual-returns',
        },
      },
      {
        name: 'unit-returns-dashboard',
        path: 'annual-returns/my-returns',
        component: () =>
          import('pages/annual-returns/UnitReturnsDashboardPage.vue'),
        meta: {
          // The My Annual Returns area is for unit-level managers only.
          can: 'ANNUAL_RETURNS:ALLOW:MANAGE_UNIT',
          // the route will only be supported when this feature flag is turned on for the current user
          requiredFeatureFlag: 'annual-returns',
        },
      },
      {
        name: 'unit-return',
        path: 'annual-returns/my-return',
        component: () => import('pages/annual-returns/UnitReturnPage.vue'),
        meta: {
          // The My Annual Returns area is for unit-level managers only.
          can: 'ANNUAL_RETURNS:ALLOW:MANAGE_UNIT',
          // the route will only be supported when this feature flag is turned on for the current user
          requiredFeatureFlag: 'annual-returns',
        },
      },
      {
        name: 'fee-rules',
        path: 'annual-returns/fee-rules',
        component: () => import('pages/annual-returns/fee-rules/IndexPage.vue'),
        meta: {
          can: 'ANNUAL_RETURN_FEE_RULES:ALLOW:VIEW_ANY',
          // the route will only be supported when this feature flag is turned on for the current user
          requiredFeatureFlag: 'annual-returns',
        },
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('pages/profile/IndexPage.vue'),
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore();
          if (authStore.isMember()) {
            next(); // proceed to profile
          } else {
            next({ name: 'groups' }); // redirect to groups
          }
        },
      },
      {
        name: 'friends',
        path: 'friends',
        component: () => import('pages/contacts/IndexPage.vue'),
      },
      {
        name: 'quick-links',
        path: 'quick-links',
        component: () => import('pages/quick-links/IndexPage.vue'),
      },
      {
        name: 'admins',
        path: 'admins',
        component: () => import('pages/admins/IndexPage.vue'),
        meta: {
          can: 'ADMINS:ALLOW:VIEW_ANY',
        },
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('layouts/AuthLayout.vue'),
    children: [
      {
        path: 'maintenance',
        name: 'maintenance',
        component: () => import('pages/auth/MaintenancePage.vue'),
      },
      {
        name: 'login',
        path: 'login',
        component: () => import('pages/auth/LoginPage.vue'),
        beforeEnter: async (to, from, next) => {
          const auth = useAuthStore();
          const isLogged = await auth.revalidateSession();

          if (isLogged) {
            // already have a valid session → go home
            return next({ name: 'home' });
          } else {
            // no session → let them see the login form
            return next();
          }
        },
      },
      {
        name: 'register',
        path: 'register',
        component: () => import('pages/auth/RegisterPage.vue'),
      },
      {
        name: 'reset',
        path: 'reset',
        component: () => import('pages/auth/SendPasswordResetPage.vue'),
        props: (route) => ({
          email: route.query.email,
        }),
      },
      {
        name: 'reset-password',
        path: 'reset-password/:token',
        component: () => import('pages/auth/ResetPasswordPage.vue'),
        props: (route) => ({
          tokenIn: route.params.token,
          emailIn: route.query.email,
        }),
        beforeEnter: (to, from, next) => {
          if (to.params.token && to.query.email) {
            next(); // proceed to the route if both parameters are present
          } else {
            next({ name: 'login' });
          }
        },
      },
    ],
  },
  // only show this in dev env
  ...(process.env.DEV
    ? [
        {
          path: '/feature-flags',
          component: () => import('layouts/BlankLayout.vue'),
          children: [
            {
              path: '',
              name: 'feature-flags',
              component: () => import('pages/feature-flags/IndexPage.vue'),
            },
          ],
        },
      ]
    : []),

  // Access denied page (401)
  {
    path: '/access-denied',
    name: 'access-denied',
    component: () => import('pages/ErrorAccessDenied.vue'),
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue'),
  },
];

export default routes;
